import { DialogTitle } from "@headlessui/react";
import { ClipboardCheckIcon } from "lucide-react";
import Error from "components/Error";
import Button from "components/buttons/Button";
import Modal from "components/modal/Modal";
import { FeedbackFormTypeEnum, QaFormRequirementEnum } from "pages/project/common/taskBase.typing";
import { useDeliverTaskForm } from "pages/project/task/components/modals/useDeliverTaskForm";
import { FeedbackFormSection } from "pages/project/task/components/modals/FeedbackFormSection";
import { useQAForm, useTaskById, useSubmitFeedbackMutation } from "query/task.query";
import { useTranslation } from "react-i18next";

const ICRTaskType = "QC 8-VALID";

interface Props {
  show: boolean;
  taskId: string;
  onClose?: () => void;
}

const FeedbackModal = ({ show, taskId, onClose }: Props) => {
  const { t } = useTranslation("translation");
  const tModal = (key: string) => t(`task.modal.feedback.${key}`);
  const tForm = (key: string) => t(`task.modal.deliver.${key}`);

  // Fetch only this specific task by ID (efficient - no bulk loading)
  const { task: selectedTask, isLoading } = useTaskById(taskId);

  const { submitFeedback: submitFeedbackMutation, isPending: isSubmitting } = useSubmitFeedbackMutation(
    taskId,
    onClose
  );

  // Determine which form to show based on feedbackFormType
  const feedbackFormType = selectedTask?.feedbackFormType ?? FeedbackFormTypeEnum.None;
  const shouldShowQAForm =
    feedbackFormType === FeedbackFormTypeEnum.QA ||
    feedbackFormType === FeedbackFormTypeEnum.STAR ||
    feedbackFormType === FeedbackFormTypeEnum.DTP;
  const shouldShowMTForm = feedbackFormType === FeedbackFormTypeEnum.MT;
  const isStarRating = selectedTask?.taskType === ICRTaskType;

  // Fetch QA form for QA/STAR/DTP forms, or for MT forms only if task has a QA profile
  const taskForFormFetching = selectedTask
    ? {
        ...selectedTask,
        shouldDisplayQAForm: shouldShowQAForm || (shouldShowMTForm && selectedTask.shouldDisplayQAForm),
      }
    : undefined;

  const { isLoading: isQAFormLoading, qaForm } = useQAForm(taskForFormFetching);

  const {
    deliver: submitFeedback,
    register,
    formState,
    qaFormRatings,
    rated3StarsOrLess,
    setValue,
    validationError,
  } = useDeliverTaskForm(
    taskId,
    qaForm,
    shouldShowQAForm,
    selectedTask?.catToolId,
    true, // isUserSupplier
    shouldShowMTForm,
    // Callback: submit feedback only (feedback type is auto-detected on backend)
    ({ comment, ratings }) => {
      submitFeedbackMutation(comment, ratings);
    },
    selectedTask?.qaFormRequirement === QaFormRequirementEnum.Optional, // Optional if qaFormRequirement === Optional
    true, // isFeedbackOnly: require form to be filled when submitting feedback
    isStarRating // isStarRating
  );

  const isPending = isSubmitting;

  if (isLoading || !selectedTask) {
    return null;
  }

  const handleClose = () => {
    // Don't call reset() here as it navigates away from the current page
    // The form will be re-initialized when the modal opens again
    onClose?.();
  };

  return (
    <>
      <Modal
        isShown={show}
        onClose={handleClose}
        maxWidthClassName={shouldShowQAForm || shouldShowMTForm ? "sm:max-w-7xl" : undefined}
      >
        <div>
          {/*Icon*/}
          <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-blue-100">
            <ClipboardCheckIcon className="size-6 text-blue-600" aria-hidden="true" />
          </div>
          {/*Header*/}
          <div className="mt-3 text-center sm:mt-5">
            <DialogTitle as="h3" className="text-color-primary text-lg font-medium leading-6 sm:text-3xl">
              <div>
                {feedbackFormType === FeedbackFormTypeEnum.MT && tModal("mt-title")}
                {feedbackFormType === FeedbackFormTypeEnum.QA && tModal("qa-title")}
                {feedbackFormType === FeedbackFormTypeEnum.STAR && tModal("star-title")}
                {feedbackFormType === FeedbackFormTypeEnum.DTP && tModal("dtp-title")}
              </div>
              <div className="text-base text-gray-600 sm:text-lg">{selectedTask.taskCode}</div>
            </DialogTitle>
          </div>

          {/*body*/}
          <div>
            <form className="mx-4 mt-6 flex flex-col space-y-5" onSubmit={submitFeedback}>
              {shouldShowQAForm || shouldShowMTForm ? (
                <FeedbackFormSection
                  qaForm={qaForm}
                  isQAFormLoading={isQAFormLoading}
                  qaFormRatings={qaFormRatings}
                  setValue={setValue}
                  feedbackFormType={feedbackFormType}
                  shouldShowQAForm={shouldShowQAForm}
                  shouldShowMTForm={shouldShowMTForm}
                  isStarRating={isStarRating}
                  register={register}
                  formState={formState}
                  rated3StarsOrLess={rated3StarsOrLess}
                  taskType={selectedTask.taskType}
                  isUserSupplier={true}
                  t={t}
                  tForm={tForm}
                />
              ) : null}

              {validationError ? <Error error={validationError} /> : null}

              <div className="ml-auto w-full max-w-3xl">
                <div className="mt-5 grid grid-cols-2 gap-3 text-sm sm:mt-6 sm:grid-flow-row-dense">
                  <Button
                    type="reset"
                    disabled={isPending}
                    color="ternary"
                    label={tModal("cancel")}
                    onClick={handleClose}
                  />
                  <Button type="submit" disabled={isPending} color="primary" label={tModal("submit")} />
                </div>
              </div>
            </form>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default FeedbackModal;
